// Paragraph-split overlay helpers.
// Splits are user-created intra-turn paragraph breaks stored as a jsonb
// array on transcripts.paragraph_splits. Each entry marks the start_ms of
// the first token of a new second-half paragraph within a Soniox speaker
// group. Render-time only; verbatim payload is unchanged per structural
// invariant 1.

import { supabase } from "./supabase";

export interface ParagraphSplit {
  id: string;            // uuid, client-generated
  split_at_ms: number;   // start_ms of the first token of the new paragraph
  name: string | null;   // display name for the second-half paragraph
  created_at: string;    // ISO timestamp
  created_by: string | null;
}

// Append a new split entry. Read-modify-write at call time so concurrent
// edits don't clobber each other (same discipline as appendSpanAttribution).
export async function appendParagraphSplit(
  meetingId: string,
  entry: ParagraphSplit,
): Promise<void> {
  const { data: row, error: readErr } = await supabase
    .from("transcripts")
    .select("paragraph_splits")
    .eq("meeting_id", meetingId)
    .maybeSingle();
  if (readErr) throw readErr;
  const current = ((row?.paragraph_splits ?? []) as ParagraphSplit[]).slice();
  current.push(entry);
  const { error: writeErr } = await supabase
    .from("transcripts")
    .update({ paragraph_splits: current })
    .eq("meeting_id", meetingId);
  if (writeErr) throw writeErr;
}

// Remove a split entry by id (rejoin). Read-modify-write per the same
// discipline as removeSpanAttribution.
export async function removeParagraphSplit(
  meetingId: string,
  id: string,
): Promise<void> {
  const { data: row, error: readErr } = await supabase
    .from("transcripts")
    .select("paragraph_splits")
    .eq("meeting_id", meetingId)
    .maybeSingle();
  if (readErr) throw readErr;
  const current = ((row?.paragraph_splits ?? []) as ParagraphSplit[]).filter(
    (e) => e.id !== id,
  );
  const { error: writeErr } = await supabase
    .from("transcripts")
    .update({ paragraph_splits: current })
    .eq("meeting_id", meetingId);
  if (writeErr) throw writeErr;
}
